import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBook, FiDownload, FiExternalLink, FiPlay, FiFileText, FiBarChart3 } = FiIcons;

const Resources = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'All Resources' },
    { id: 'textbooks', label: 'Textbooks' },
    { id: 'case-studies', label: 'Case Studies' },
    { id: 'videos', label: 'Videos' },
    { id: 'articles', label: 'Articles' },
    { id: 'tools', label: 'Tools' }
  ];

  const resources = [
    {
      id: 1,
      title: 'Marketing Management: Analysis, Planning, and Control',
      type: 'textbooks',
      format: 'PDF',
      description: 'Comprehensive guide to marketing principles and strategies',
      icon: FiBook,
      downloadable: true,
      featured: true
    },
    {
      id: 2,
      title: 'Nike\'s Sports Marketing Strategy Case Study',
      type: 'case-studies',
      format: 'PDF',
      description: 'In-depth analysis of Nike\'s marketing approach in sports',
      icon: FiFileText,
      downloadable: true,
      featured: true
    },
    {
      id: 3,
      title: 'Digital Marketing Fundamentals',
      type: 'videos',
      format: 'Video',
      description: 'Video series covering digital marketing basics',
      icon: FiPlay,
      downloadable: false,
      featured: false
    },
    {
      id: 4,
      title: 'Consumer Behavior in Sports Marketing',
      type: 'articles',
      format: 'Article',
      description: 'Research article on consumer psychology in sports',
      icon: FiFileText,
      downloadable: true,
      featured: false
    },
    {
      id: 5,
      title: 'Marketing Analytics Dashboard Template',
      type: 'tools',
      format: 'Excel',
      description: 'Ready-to-use template for tracking marketing metrics',
      icon: FiBarChart3,
      downloadable: true,
      featured: true
    },
    {
      id: 6,
      title: 'Social Media Marketing Guide',
      type: 'textbooks',
      format: 'PDF',
      description: 'Complete guide to social media marketing strategies',
      icon: FiBook,
      downloadable: true,
      featured: false
    }
  ];

  const filteredResources = resources.filter(resource => 
    selectedCategory === 'all' || resource.type === selectedCategory
  );

  const featuredResources = resources.filter(resource => resource.featured);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Learning Resources</h1>
        <p className="text-gray-600">Access comprehensive materials to enhance your marketing knowledge</p>
      </motion.div>

      {/* Featured Resources */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="mb-12"
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Resources</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {featuredResources.map((resource, index) => (
            <motion.div
              key={resource.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-6 border border-blue-200"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={resource.icon} className="text-blue-600 text-xl" />
                </div>
                <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-medium">
                  Featured
                </span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{resource.title}</h3>
              <p className="text-gray-600 mb-4">{resource.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">{resource.format}</span>
                <button className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1">
                  <SafeIcon icon={resource.downloadable ? FiDownload : FiExternalLink} />
                  <span>{resource.downloadable ? 'Download' : 'View'}</span>
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Category Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="mb-8"
      >
        <div className="flex flex-wrap gap-2">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategory === category.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Resources Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {filteredResources.map((resource, index) => (
          <motion.div
            key={resource.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <SafeIcon icon={resource.icon} className="text-gray-600 text-xl" />
              </div>
              {resource.featured && (
                <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-medium">
                  Featured
                </span>
              )}
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{resource.title}</h3>
            <p className="text-gray-600 mb-4">{resource.description}</p>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">{resource.format}</span>
              <button className="text-blue-600 hover:text-blue-700 font-medium flex items-center space-x-1">
                <SafeIcon icon={resource.downloadable ? FiDownload : FiExternalLink} />
                <span>{resource.downloadable ? 'Download' : 'View'}</span>
              </button>
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Study Tips */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className="mt-16 bg-white rounded-lg shadow-md p-8"
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Study Tips</h2>
        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-lg font-semibold text-blue-600 mb-3">Effective Reading</h3>
            <ul className="space-y-2 text-gray-600">
              <li>• Take notes while reading case studies</li>
              <li>• Identify key marketing concepts and strategies</li>
              <li>• Connect theory with real-world examples</li>
              <li>• Review and summarize main points</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-blue-600 mb-3">Application Practice</h3>
            <ul className="space-y-2 text-gray-600">
              <li>• Apply concepts to current market trends</li>
              <li>• Analyze competitor marketing strategies</li>
              <li>• Create your own marketing scenarios</li>
              <li>• Discuss insights with classmates</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Resources;